import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { useDashboardData } from "../../../context/DashboardContext";
import type { WidgetComponentProps } from "../types";

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(value);

const PercentIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
  </svg>
);

export default function CommissionWidget({ config, title }: WidgetComponentProps) {
  const { metrics } = useDashboardData();
  const cardRef = useRef<HTMLDivElement>(null);
  const [cardSize, setCardSize] = useState({ width: 0, height: 0 });

  const cashKey = (config.cashKey as string) || "totalCashCollected";
  const rate = (config.rate as number) || 0.1;
  const colorTheme = (config.colorTheme as string) || "indigo";
  const description = (config.description as string) || `${(rate * 100).toFixed(0)}% of total cash collected`;

  const cash = metrics[cashKey] ?? 0;
  const commission = cash * rate;

  const colorMap: Record<string, { gradient: string; border: string; shadow: string; text: string }> = {
    indigo: {
      gradient: "from-indigo-500/20 via-indigo-500/10 to-transparent",
      border: "border-indigo-500/20",
      shadow: "shadow-[0_0_30px_-5px_rgba(99,102,241,0.3)] hover:shadow-[0_0_50px_-5px_rgba(99,102,241,0.5)]",
      text: "text-indigo-400",
    },
    emerald: {
      gradient: "from-emerald-500/20 via-emerald-500/10 to-transparent",
      border: "border-emerald-500/20",
      shadow: "shadow-[0_0_30px_-5px_rgba(52,211,153,0.3)] hover:shadow-[0_0_50px_-5px_rgba(52,211,153,0.5)]",
      text: "text-emerald-400",
    },
  };

  const colors = colorMap[colorTheme] || colorMap.indigo;
  const showDescription = Boolean(description) && cardSize.width >= 320 && cardSize.height >= 145;

  useEffect(() => {
    const element = cardRef.current;
    if (!element) return;

    const updateSize = () => {
      setCardSize({
        width: element.clientWidth,
        height: element.clientHeight,
      });
    };

    updateSize();

    const observer = new ResizeObserver(updateSize);
    observer.observe(element);

    return () => observer.disconnect();
  }, []);

  return (
    <motion.div
      ref={cardRef}
      whileHover={{ y: -4, transition: { duration: 0.2 } }}
      className={`relative overflow-hidden bg-gradient-to-br ${colors.gradient} ${colors.border} border rounded-2xl p-6 ${colors.shadow} transition-shadow duration-500 h-full`}
    >
      <div className="relative">
        <div className="flex items-center gap-2 mb-3">
          <motion.div
            className={`size-5 ${colors.text}`}
            whileHover={{ scale: 1.1, rotate: 5 }}
            transition={{ type: "spring", stiffness: 400 }}
          >
            <PercentIcon />
          </motion.div>
          <span className="text-xs md:text-sm font-semibold text-foreground/80 uppercase tracking-wider">
            {title}
          </span>
        </div>
        <p className="text-2xl md:text-4xl font-bold text-foreground tracking-tight">
          {formatCurrency(commission)}
        </p>
        {showDescription && (
          <p className="text-xs text-muted-foreground mt-1.5 md:mt-2">{description}</p>
        )}
      </div>
    </motion.div>
  );
}
